import React, { useState, useEffect, useContext } from "react";
import "../../assets/css/Test.css";
import axios from "axios";
import { useSearchParams, useNavigate } from "react-router-dom";
import FooterPage from "../footer/FooterPage";
import { Container } from "react-bootstrap";
import { AuthContext } from "../context/AuthContext";

// Define the base URL for your API
const API_BASE_URL = "https://brainrock.in/brainrock/backend/api";

function QuizTest() {
  // Get user_id and quiz_id from URL search parameters or localStorage
  const [searchParams] = useSearchParams();
  const urlUserId = searchParams.get("user_id");
  const urlQuizId = searchParams.get("quiz_id");
  const storageUserId = localStorage.getItem("quiz_user_id");
  const storageQuizId = localStorage.getItem("quiz_quiz_id");
  const userId = urlUserId || storageUserId;
  const quizId = urlQuizId || storageQuizId;
  const navigate = useNavigate();

  // Handle User ID or Quiz ID change to ensure latest data is used
  useEffect(() => {
    if (urlUserId && urlUserId !== storageUserId) {
      localStorage.setItem("quiz_user_id", urlUserId);
    }
    if (urlQuizId && urlQuizId !== storageQuizId) {
      localStorage.setItem("quiz_quiz_id", urlQuizId);
    }
  }, [urlUserId, urlQuizId, storageUserId, storageQuizId]);
  
  // Check for payment success parameters in URL
  const paymentSuccess = searchParams.get("payment_success");
  const paymentMethod = searchParams.get("payment_method");
  
  // If payment was successful and we have user_id and quiz_id, proceed
  if (paymentSuccess === "true" && userId && quizId) {
    console.log("QuizTest component - Payment successful via:", paymentMethod);
  } else if (paymentSuccess === "false" || paymentSuccess === "cancel") {
    // If payment was canceled or failed, close the tab
    console.log("QuizTest component - Payment canceled or failed");
    window.close();
  }

  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [score, setScore] = useState(0);
  const [showResults, setShowResults] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [attemptId, setAttemptId] = useState(null);
  const [userAnswers, setUserAnswers] = useState([]);
  const [timeLeft, setTimeLeft] = useState(30);
  const [testResult, setTestResult] = useState(null);
  const [tabSwitchWarning, setTabSwitchWarning] = useState(false);
  const [tabSwitchCount, setTabSwitchCount] = useState(0);
  const [animateScore, setAnimateScore] = useState(false);
  const [showWinnerForm, setShowWinnerForm] = useState(false);
  const [winnerFormData, setWinnerFormData] = useState({
    phone: localStorage.getItem("quiz_user_phone") || "",
    password: "",
    account_holder_name: "",
    account_number: "",
    ifsc_code: "",
  });
  const [showWrongAnswersModal, setShowWrongAnswersModal] = useState(false);
  const [wrongAnswers, setWrongAnswers] = useState([]);
  const [certificateUrl, setCertificateUrl] = useState(null);
  
  // Language preference from localStorage
  const [language, setLanguage] = useState(() => localStorage.getItem("quiz_language") || "english");

  // Get questions from localStorage instead of API
  useEffect(() => {
    const loadQuestions = () => {
      try {
        const storedQuestions = localStorage.getItem("quiz_questions");
        const storedAttemptId = localStorage.getItem("quiz_attempt_id");
        
        if (storedQuestions) {
          const parsedQuestions = JSON.parse(storedQuestions);
          setQuestions(parsedQuestions);
          setAttemptId(storedAttemptId || null);
          setLoading(false);
        } else {
          // Fallback: fetch from API if no stored questions
          startQuizFromAPI();
        }
      } catch (err) {
        console.error("Error loading questions from localStorage:", err);
        startQuizFromAPI();
      }
    };

    const startQuizFromAPI = async () => {
      try {
        if (!userId || !quizId) {
          throw new Error("User ID or Quiz ID not found. Please register first.");
        }

        const response = await axios.post(
          "https://brainrock.in/brainrock/backend/api/quiz-start/",
          { user_id: userId, quiz_id: quizId },
          {
            withCredentials: true,
            headers: {
              "Content-Type": "application/json",
            },
          },
        );

        if (response.data.status) {
          setQuestions(response.data.questions);
          setAttemptId(response.data.attempt_id);
          // Store for potential page refresh
          localStorage.setItem("quiz_questions", JSON.stringify(response.data.questions));
          localStorage.setItem("quiz_attempt_id", response.data.attempt_id);
        } else {
          throw new Error(response.data.message || "Failed to start quiz");
        }
      } catch (err) {
        let errorMessage = "Failed to start quiz";
        if (err.response && err.response.data && err.response.data.message) {
          errorMessage = err.response.data.message;
        } else if (err.request) {
          errorMessage = "No response from server. Please check your connection.";
        } else {
          errorMessage = err.message;
        }
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    loadQuestions();
  }, [userId, quizId]);

  // Timer effect
  useEffect(() => {
    if (loading || showResults || !questions.length) return;

    const timer = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          // Time's up, move to next question
          handleNextQuestion();
          return 10; // Reset timer for next question
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [currentQuestion, loading, showResults, questions.length]);

  // Reset timer only when current question changes
  useEffect(() => {
    setTimeLeft(10);
  }, [currentQuestion]);

  // Tab switch detection
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && !loading && !showResults) {
        // User switched tabs
        setTabSwitchCount((prevCount) => {
          const newCount = prevCount + 1;

          if (newCount === 1) {
            // First warning
            setTabSwitchWarning(true);
            console.log("Quiz warning: First tab switch");
          } else if (newCount === 2) {
            // Second time, fail the quiz
            setShowResults(true);
            setScore(0);
            console.log("Quiz failed: Second tab switch");
          }

          return newCount;
        });
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [loading, showResults]);

  // Trigger score animation when results are shown
  useEffect(() => {
    if (showResults) {
      setTimeout(() => setAnimateScore(true), 100);
    }
  }, [showResults]);

  const handleOptionSelect = (optionIndex) => {
    setSelectedOption(optionIndex);
  };

  const handleNextQuestion = () => {
    // Save user's answer for current question (even if no answer selected)
    const newAnswers = [
      ...userAnswers,
      {
        question_id: questions[currentQuestion].id,
        selected_option: selectedOption,
      },
    ];
    setUserAnswers(newAnswers);

    // Check if answer is correct and update score
    if (selectedOption === questions[currentQuestion].correct_answer) {
      setScore(score + 1);
    }

    const nextQuestion = currentQuestion + 1;
    if (nextQuestion < questions.length) {
      setCurrentQuestion(nextQuestion);
      setSelectedOption(null);
    } else {
      // All questions completed, submit test
      submitQuiz(newAnswers);
    }
  };

  // Submit quiz to API
  const submitQuiz = async (answers) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/submit-quiz/`,
        {
          user_id: userId,
          quiz_id: quizId,
          answers: answers,
        },
        {
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      console.log("Quiz submission successful:", response.data);
      // Save API response data for display
      setTestResult(response.data);
      
      // Check if certificate is available
      if (response.data.certificate) {
        const certificatePath = response.data.certificate.startsWith('http') 
          ? response.data.certificate 
          : `https://brainrock.in/brainrock/backend${response.data.certificate}`;
        setCertificateUrl(certificatePath);
      }
      
      setShowResults(true);
    } catch (err) {
      console.error("Error submitting quiz:", err);
      setError(err.message);
    }
  };

  const { user } = useContext(AuthContext);

  // Handle claim reward
  const handleClaimReward = async () => {
    try {
      const response = await axios.post(
        "https://brainrock.in/brainrock/backend/api/quiz/add-cashback/",
        { user_id: userId, quiz_id: quizId },
        {
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.status) {
        alert("Congratulations! Your reward has been claimed successfully.");
        // Redirect to user dashboard
        navigate("/UserDashBoard");
      } else {
        throw new Error(response.data.message || "Failed to claim reward");
      }
    } catch (error) {
      console.error("Error claiming reward:", error);
      alert("Failed to claim reward. Please try again.");
    }
  };

  const handleCloseTest = () => {
    // Clear localStorage
    localStorage.removeItem("quiz_user_id");
    localStorage.removeItem("quiz_quiz_id");
    
    // Redirect to KheloJito page (main quiz section) instead of closing tab
    navigate("/KheloJito");
  };

  // Function to find and display wrong answers
  const handleShowWrongAnswers = () => {
    const wrong = [];
    userAnswers.forEach((answer, index) => {
      // Add safety checks to prevent undefined errors
      const question = questions[index];
      if (!question) return;
      
      const userSelectedOption = answer.selected_option;
      const correctAnswer = question.correct_answer;
      
      // Skip if user didn't answer or question doesn't have correct_answer
      if (userSelectedOption === null || userSelectedOption === undefined || correctAnswer === undefined) return;
      
      if (userSelectedOption !== correctAnswer) {
        wrong.push({
          question: question.question_text || "Question not available",
          question_hindi_text: question.question_hindi_text || "",
          userAnswer: question.options && question.options[userSelectedOption] ? question.options[userSelectedOption] : "Not answered",
          correctAnswer: question.options && question.options[correctAnswer] ? question.options[correctAnswer] : "N/A",
          userAnswerHindi: question.options_hindi && question.options_hindi[userSelectedOption] ? question.options_hindi[userSelectedOption] : "",
          correctAnswerHindi: question.options_hindi && question.options_hindi[correctAnswer] ? question.options_hindi[correctAnswer] : "",
          questionNumber: index + 1
        });
      }
    });
    setWrongAnswers(wrong);
    setShowWrongAnswersModal(true);
  };

  // Handle winner form input changes
  const handleWinnerFormChange = (e) => {
    const { name, value } = e.target;
    setWinnerFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Submit winner form to API
  const submitWinnerForm = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(
        "https://brainrock.in/brainrock/backend/api/quiz-winners/",
        {
          user_id: userId,
          quiz_id: quizId,
          score: testResult?.score || score,
          ...winnerFormData
        },
        {
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
          },
        },
      ); 

      console.log("Winner form submission successful:", response.data);
      // Show success message and redirect to login page
      alert("Congratulations! Your details have been submitted successfully. You will receive your winning amount soon.");
      localStorage.removeItem("quiz_user_id");
      localStorage.removeItem("quiz_quiz_id");
      navigate("/login");
    } catch (err) {
      console.error("Error submitting winner form:", err);
      alert("Failed to submit details. Please try again.");
    }
  };

  if (loading) {
    return (
      <div className="test-container">
        <div className="test-card">
          <div className="loading">Loading questions...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="test-container">
        <div className="test-card">
          <div className="error"> {error}</div>
        </div>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="test-container">
        <div className="test-card">
          <div className="no-questions">
            No questions available. Please check back later.
          </div>
        </div>
      </div>
    );
  }

  if (showResults) {
    // Check if quiz was failed due to tab switch
    const isTabSwitchFailed =
      score === 0 && userAnswers.length < questions.length;
    // Calculate percentage
    const calculatedScore = testResult?.score !== undefined ? testResult.score : score;
    const percentage = Math.round((calculatedScore / questions.length) * 100);
    console.log("Calculated Score:", calculatedScore);
    // Debug information
    console.log("Test Result:", testResult);
    console.log("Score:", testResult?.score || score);
    console.log("Total Questions:", questions.length);
    console.log("Percentage:", percentage);
    
    // Determine if passed based on API response or score
    const isPassed = testResult
      ? (testResult.status === "passed" || testResult.status === "success" || percentage === 100)
      : percentage >= 60;

    // If user passed and showWinnerForm is true, display the winner form
    if (isPassed && showWinnerForm) {
      return (
        <div className="test-container">
          <div className="test-card results-card">
            <div className="results-header">
              <h1 className="results-title">Congratulations! You Won!</h1>
              <p className="results-subtitle">Please fill in your details to receive your winning amount</p>
            </div>

            <form className="winner-form" onSubmit={submitWinnerForm}>
              <div className="form-group">
                <label>User ID</label>
                <input
                  type="text"
                  name="user_id"
                  value={userId}
                  disabled
                  className="form-control"
                />
              </div>
              <div className="form-group">
                <label>Quiz ID</label>
                <input
                  type="text"
                  name="quiz_id"
                  value={quizId}
                  disabled
                  className="form-control"
                />
              </div>
              <div className="form-group">
                <label>Score</label>
                <input
                  type="text"
                  name="score"
                  value={testResult?.score || score}
                  disabled
                  className="form-control"
                />
              </div>
              <div className="form-group">
                <label>Phone Number</label>
                <input
                  type="tel"
                  name="phone"
                  value={winnerFormData.phone}
                  disabled
                  className="form-control"
                  required
                />
              </div>
              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  name="password"
                  value={winnerFormData.password}
                  onChange={handleWinnerFormChange}
                  placeholder="Enter password"
                  className="form-control"
                  required
                />
              </div>
              <div className="form-group">
                <label>Account Holder Name</label>
                <input
                  type="text"
                  name="account_holder_name"
                  value={winnerFormData.account_holder_name}
                  onChange={handleWinnerFormChange}
                  placeholder="Enter account holder name"
                  className="form-control"
                  required
                />
              </div>
              <div className="form-group">
                <label>Account Number</label>
                <input
                  type="text"
                  name="account_number"
                  value={winnerFormData.account_number}
                  onChange={handleWinnerFormChange}
                  placeholder="Enter account number"
                  className="form-control"
                  required
                />
              </div>
              <div className="form-group">
                <label>IFSC Code</label>
                <input
                  type="text"
                  name="ifsc_code"
                  value={winnerFormData.ifsc_code}
                  onChange={handleWinnerFormChange}
                  placeholder="Enter IFSC code"
                  className="form-control"
                  required
                />
              </div>

              <div className="form-actions">
                <button type="submit" className="submit-button">
                  Submit Details
                </button>
                <button type="button" className="cancel-button" onClick={() => {
                  setShowWinnerForm(false);
                  // Redirect based on user authorization
                  if (user && user.unique_id) {
                    navigate("/UserDashBoard");
                  } else {
                    navigate("/KheloJito");
                  }
                }}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      );
    }

    // Otherwise, show results page
    return (
      <div className="test-container">
        <div className="test-card results-card">
          <div className="results-header">
            <h1 className="results-title">Quiz Complete!</h1>
          </div>

          <div className="score-container">
            <div className="score">{testResult?.score || score}</div>
            <div className="score-label">
              {isPassed ? "You Passed!" : "You Failed"}
            </div>
            <div className="percentage">
              {percentage}%
            </div>
          </div>

          {isPassed ? (
            <div className="success-message">
              <p>Congratulations! You have passed the quiz.</p>
              {certificateUrl && (
                <div className="certificate-section">
                  <h3>Your Certificate</h3>
                  <p>Click the link below to view and download your certificate:</p>
                  <a
                    href={certificateUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="certificate-link"
                  >
                    View Certificate
                  </a>
                </div>
              )}
              <button className="continue-button" onClick={handleClaimReward}>
                Claim Reward
              </button>
            </div>
          ) : (
            
            <div className="failure-message">
              <p>Sorry, you did not pass the quiz this time.</p>
              <button className="restart-button" onClick={handleCloseTest}>
                Close Test
              </button>
            </div>
          )}

          {userAnswers.length === questions.length && !isPassed && (
            <div className="wrong-answers-section">
              <button className="wrong-answers" onClick={handleShowWrongAnswers}>
                View Wrong Answers
              </button>
            </div>
          )}

          {isTabSwitchFailed && (
            <div className="tab-switch-failure">
              <h3>Test Failed Due to Tab Switching</h3>
              <p>Your quiz was terminated because you switched tabs multiple times.</p>
            </div>
          )}

          {/* Wrong Answers Modal */}
          {showWrongAnswersModal && (
            <div className="wrong-answers-modal">
              <div className="modal-content">
                <div className="modal-header">
                  <h2>Wrong Answers ({wrongAnswers.length})</h2>
                  <button className="close-button" onClick={() => setShowWrongAnswersModal(false)}>
                    ×
                  </button>
                </div>
                <div className="modal-body">
                  {wrongAnswers.length === 0 ? (
                    <div className="no-wrong-answers">
                      Congratulations! You answered all questions correctly.
                    </div>
                  ) : (
                    wrongAnswers.map((item, index) => (
                      <div key={index} className="wrong-answer-item">
                        <div className="question-number">Question {item.questionNumber}:</div>
                        <div className="question-text">{item.question}</div>
                        {item.question_hindi_text && (
                          <div className="hindi-question mt-1">
                            <small className="text-muted">{item.question_hindi_text}</small>
                          </div>
                        )}
                        <div className="answer-container">
                          <div className="user-answer">
                            <span className="label">Your Answer:</span>
                            <span className="answer-text">{item.userAnswer}</span>
                            {item.userAnswerHindi && (
                              <div className="text-muted mt-1">
                                <small>{item.userAnswerHindi}</small>
                              </div>
                            )}
                          </div>
                          <div className="correct-answer">
                            <span className="label">Correct Answer:</span>
                            <span className="answer-text">{item.correctAnswer}</span>
                            {item.correctAnswerHindi && (
                              <div className="text-muted mt-1">
                                <small>{item.correctAnswerHindi}</small>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <div className="modal-footer">
                  <button className="close-button" onClick={() => setShowWrongAnswersModal(false)}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Display quiz questions
  return (
    <div className="test-container">
      <div className="test-card">
        <div className="question-header">
          <div className="question-number">
            Question {currentQuestion + 1} of {questions.length}
          </div>
          <div className="timer">
            Time Left: <span className="timer-value">{timeLeft}s</span>
          </div>
        </div>

        <div className="question-container">
          <div className="question-text">
            {questions[currentQuestion].question_text}
            {questions[currentQuestion].question_text_hindi && (
              <div className="question-text-hindi mt-2">
                {questions[currentQuestion].question_text_hindi}
              </div>
            )}
          </div>
          <div className="options-container">
            {questions[currentQuestion].options.map((option, index) => (
              <div
                key={index}
                className={`option ${
                  selectedOption === index ? "selected" : ""
                }`}
                onClick={() => handleOptionSelect(index)}
              >
                <div className="option-letter">{String.fromCharCode(65 + index)}</div>
                <div className="option-text">
                  {option}
                  {questions[currentQuestion].options_hindi && questions[currentQuestion].options_hindi[index] && (
                    <div className="option-text-hindi mt-1">
                      {questions[currentQuestion].options_hindi[index]}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="navigation-container">
          <button
            className="next-button"
            onClick={handleNextQuestion}
            disabled={selectedOption === null}
          >
            {currentQuestion === questions.length - 1 ? "Submit Quiz" : "Next Question"}
          </button>
        </div>

        {tabSwitchWarning && (
          <div className="tab-switch-warning">
            <p>⚠️ Warning: Do not switch tabs during the quiz. This is your first warning.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default QuizTest;
